/**
 * RAPID CONTACT LIST — Lista server-side dei contatti rapidi (DataGrid).
 *
 * Stessa struttura di PersonList (search + pagination + sort server-side,
 * delete con useListDeleteHandler). Differenze:
 *   - email/phone/companyName/fairEvent sono campi scalari reali del DTO
 *     (RapidContact non ha nested array): niente valueGetter su sub-resource.
 *   - La creazione NON apre un drawer: il bottone "Nuovo" naviga alla pagina
 *     full-page RapidContactPage (/add-rapid-contact).
 *   - Il nome è un Link alla pagina di dettaglio (`/rapid-contact/:id`), che riusa
 *     la form di inserimento: editabile se il contatto è da importare, in sola
 *     lettura se già importato (toggle admin "Importati").
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type GridColDef } from '@mui/x-data-grid';
import { useServerSearch, useServerListParams, useListDeleteHandler, formatDateTime } from '@crm/shared';
import { ServerDataGrid } from '../../components/ServerDataGrid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchRapidContacts, deleteRapidContact, importRapidContacts } from './rapidContactThunks';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { SearchGlyph, PlusGlyph, ImportGlyph, TrashGlyph, CheckGlyph } from '../../components/icons';
import { useToast } from '../../components/useToast';
import { RapidContactImportDialog } from './RapidContactImportDialog';

export default function RapidContactList() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const toast = useToast();
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Vista importati (TS-100): toggle admin-only tra i contatti ancora da importare
  // (false, default) e quelli già importati (true, sola lettura).
  const [showImported, setShowImported] = useState(false);
  const isAdmin = useAppSelector((s) => s.auth.user?.administrator) ?? false;
  const { items, status, error, totalCount } = useAppSelector((state) => state.rapidContacts);
  // Server-side search condivisa (min 3 char + debounce 300ms).
  const { search, setSearch, query } = useServerSearch();
  // Pagination + sort state comune a tutte le liste paginate server-side.
  // Ordinamento di default: data di creazione discendente (i più recenti in cima).
  const {
    paginationModel, setPaginationModel,
    sortModel, setSortModel,
    resetPage,
    backendParams,
  } = useServerListParams({ initialSort: [{ field: 'createDate', sort: 'desc' }] });

  // Reset page=0 quando cambia il filtro search o la vista (importati/da importare):
  // altrimenti finiresti su una pagina che potrebbe essere oltre il nuovo totalCount.
  useEffect(() => { resetPage(); }, [query, showImported, resetPage]);

  useEffect(() => {
    dispatch(fetchRapidContacts({ ...backendParams, search: query || undefined, imported: showImported || undefined }));
  }, [backendParams, query, showImported, dispatch]);

  // Delete + refetch coerente con totalCount/paginazione (vedi PersonList).
  const handleDelete = useListDeleteHandler({
    deleteThunk: deleteRapidContact,
    fetchThunk: fetchRapidContacts,
    fetchParams: { ...backendParams, search: query || undefined, imported: showImported || undefined },
    itemsLength: items.length,
    page: paginationModel.page,
    resetPage,
    dispatch,
  });

  // Import massivo: importa TUTTI i contatti fiera non ancora importati del sito (nessuna
  // selezione). La lista mostra solo i non importati (filtro backend), quindi totalCount è
  // quanti ne restano da importare. Dopo l'import si rifetcha: gli importati spariscono.
  // FILO-67: la conferma chiede anche la data di inizio dei richiami, da cui si contano le
  // scadenze dei task di follow-up: per questo una finestra apposita e non il ConfirmDialog.
  async function handleImport(callStartDate: string) {
    setImportOpen(false);
    setImporting(true);
    try {
      const { imported, tasks, failed } = await dispatch(importRapidContacts(callStartDate)).unwrap();
      toast({
        severity: 'success',
        message: tasks > 0
          ? t('rapidContact.importDoneWithTasks', { count: imported, tasks })
          : t('rapidContact.importDone', { count: imported }),
      });
      // TS-137: chi fallisce non ferma gli altri e resta in lista, da correggere.
      if (failed.length > 0) {
        toast({
          severity: 'warning',
          message: t('rapidContact.importFailed', {
            count: failed.length,
            details: failed.map((f) => `${f.name}: ${f.message}`).join('; '),
          }),
        });
      }
      dispatch(fetchRapidContacts({ ...backendParams, search: query || undefined, imported: showImported || undefined }));
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setImporting(false);
    }
  }

  if (status === 'failed') return <p className="text-error">{t('common.error', { message: error })}</p>;

  return (
    <div>
      {/* Testata come la lista Contatti, che e' la pagina di riferimento del
          nuovo design: titolo e conteggio a sinistra, ricerca e azioni a
          destra, il tutto fuori dalla card. */}
      <div className="page-head">
        <div className="page-head__left">
          <div>
            <h1 className="page-title">{t('rapidContact.listTitle')}</h1>
            <span className="page-sub">{t('common.record', { count: totalCount })}</span>
          </div>
          {/* Le due viste sono un interruttore segmentato accanto al titolo, come
              Tabella/Bacheca nei task: sono due modi di guardare la stessa lista,
              non un'opzione fra le azioni. Solo per gli amministratori; la vista
              importati e' in sola lettura, quindi li' Importa e Aggiungi si
              spengono. */}
          {isAdmin && (
            <div className="viewtoggle" role="tablist" aria-label={t('rapidContact.viewFilter.label')}>
              <button
                type="button"
                className={!showImported ? 'on' : undefined}
                aria-pressed={!showImported}
                onClick={() => setShowImported(false)}
              >
                <ImportGlyph /> {t('rapidContact.viewFilter.toImport')}
              </button>
              <button
                type="button"
                className={showImported ? 'on' : undefined}
                aria-pressed={showImported}
                onClick={() => setShowImported(true)}
              >
                <CheckGlyph /> {t('rapidContact.viewFilter.imported')}
              </button>
            </div>
          )}
        </div>
        <div className="page-actions">
          <div className="searchline">
            <SearchGlyph />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('rapidContact.searchPlaceholder')}
              aria-label={t('rapidContact.searchPlaceholder')}
            />
          </div>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setImportOpen(true)}
            disabled={showImported || totalCount === 0 || importing}
          >
            <ImportGlyph /> {t('rapidContact.import')}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/add-rapid-contact')}
            disabled={showImported}
          >
            <PlusGlyph /> {t('rapidContact.title')}
          </button>
        </div>
      </div>

      <div className="list-card">
        {status === 'loading' && <p className="list-message">{t('common.loading')}</p>}
        {status === 'succeeded' && items.length === 0 && (
          <p className="text-muted list-message">{t('rapidContact.noContacts')}</p>
        )}
        {items.length > 0 && (
          <ServerDataGrid
            rows={items}
            columns={rapidContactColumns(confirm, t, handleDelete, showImported)}
            getRowId={(row) => row.rapidContactId}
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            loading={status === 'loading'}
            getRowHeight={() => 64}
            className="tk-grid"
          />
        )}
      </div>
      <RapidContactImportDialog
        open={importOpen}
        count={totalCount}
        onCancel={() => setImportOpen(false)}
        onConfirm={handleImport}
      />
      <ConfirmDialog open={confirm.isOpen} message={confirm.message} {...confirm.options} onConfirm={confirm.confirm} onCancel={confirm.cancel} />
    </div>
  );
}

function rapidContactColumns(
  confirm: ReturnType<typeof useConfirmDialog>,
  t: (key: string, opts?: Record<string, unknown>) => string,
  handleDelete: (rapidContactId: number) => void,
  imported: boolean,
): GridColDef[] {
  return [
    {
      // `field: lastName` e' un campo reale del DTO: il sort server-side
      // (sort=lastName:asc) funziona sia sul mock che su Liferay. Il valueGetter
      // compone "FirstName LastName" solo per la visualizzazione. Il Link apre il
      // dettaglio (form riusata): editabile se da importare, sola lettura se importato.
      field: 'lastName', headerName: t('rapidContact.columns.name'), flex: 1,
      valueGetter: (_value: unknown, row: { firstName: string; lastName: string }) => `${row.firstName} ${row.lastName}`,
      renderCell: (params) => (
        <span className="lc-name">
          <span className="lc-av">
            {`${params.row.firstName?.[0] ?? ''}${params.row.lastName?.[0] ?? ''}`.toUpperCase() || '—'}
          </span>
          <Link to={`/rapid-contact/${params.row.rapidContactId}`} className="nm">
            {params.value}
          </Link>
        </span>
      ),
    },
    {
      field: 'email', headerName: t('rapidContact.columns.email'), width: 200, sortable: false,
      valueGetter: (_value: unknown, row: { email: string | null }) => row.email ?? '—',
      renderCell: (params) => <span className="lc-email">{params.value as string}</span>,
    },
    {
      field: 'phone', headerName: t('rapidContact.columns.phone'), width: 150, sortable: false,
      valueGetter: (_value: unknown, row: { phone: string | null }) => row.phone ?? '—',
      renderCell: (params) => <span className="lc-phone">{params.value as string}</span>,
    },
    {
      field: 'companyName', headerName: t('rapidContact.columns.company'), width: 180, sortable: false,
      valueGetter: (_value: unknown, row: { companyName: string | null }) => row.companyName ?? '—',
    },
    {
      field: 'fairEvent', headerName: t('rapidContact.columns.event'), width: 160, sortable: false,
      valueGetter: (_value: unknown, row: { fairEvent: string | null }) => row.fairEvent ?? '—',
    },
    {
      // Campo reale (audit): ordinabile server-side. È il sort di default (desc).
      field: 'createDate', headerName: t('rapidContact.columns.createDate'), width: 170,
      renderCell: (params) => (
        <span className="cell-mono">{params.value ? formatDateTime(params.value as string) : '—'}</span>
      ),
    },
    {
      // Azione a icona come nella lista contatti, in tutte e due le viste (FILO-73): anche un
      // contatto già importato si deve poter togliere. Si cancella solo il record della fiera:
      // la persona e l'azienda create dall'importazione restano, e la conferma lo dice.
      field: 'actions', headerName: t('rapidContact.columns.actions'), sortable: false,
      width: 100, align: 'right', headerAlign: 'right',
      renderCell: (params: { row: { firstName: string; lastName: string; rapidContactId: number } }) => (
        <span className="lc-actions">
          <button
            type="button"
            className="lc-act del"
            aria-label={t('common.delete')}
            onClick={() => confirm.requestConfirm(
              t(imported ? 'rapidContact.deleteConfirmImported' : 'rapidContact.deleteConfirm',
                { name: `${params.row.firstName} ${params.row.lastName}`.trim() }),
              () => handleDelete(params.row.rapidContactId),
            )}
          >
            <TrashGlyph />
          </button>
        </span>
      ),
    } as GridColDef,
  ];
}
